import { useState, useEffect } from "react";
import api from "../api/client";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../contexts/AuthContext";

const LABELS = {
  student: [
    { key: "email_messages", title: "Messages", desc: "Recevoir un email quand mon Major m'écrit" },
    { key: "email_parcours", title: "Parcours", desc: "Nouvelles sous-étapes, notes et validations d'étapes" },
    { key: "email_deadlines", title: "Échéances", desc: "Rappel 3 jours avant, le jour J, et si l'échéance est dépassée" },
    { key: "email_inactivity", title: "Relances", desc: "Un message d'encouragement après 7 jours sans activité" },
  ],
  coach: [
    { key: "email_messages", title: "Messages", desc: "Recevoir un email quand un élève m'écrit" },
    { key: "email_parcours", title: "Parcours", desc: "Notes et validations de sous-étapes par mes élèves" },
    { key: "email_coach_digest", title: "Point quotidien", desc: "Un seul email par jour : retards, élèves inactifs, échéances et RDV à venir" },
  ],
  admin: [
    { key: "email_messages", title: "Messages", desc: "Recevoir un email quand un utilisateur m'écrit" },
    { key: "email_parcours", title: "Parcours", desc: "Notes et validations d'étapes sur les dossiers" },
  ],
};

export default function NotificationPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const role = user?.role || "student";

  useEffect(() => {
    api.get("/notification-preferences")
      .then(r => setPrefs(r.data.preferences))
      .catch(() => setPrefs({}));
  }, []);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(key);
    try {
      await api.put("/notification-preferences", { [key]: next[key] });
      toast({ title: next[key] ? "Notification activée" : "Notification désactivée" });
    } catch (e) {
      setPrefs(prefs);
      toast({ variant: "destructive", title: "Erreur d'enregistrement" });
    } finally {
      setSaving(null);
    }
  };

  const items = LABELS[role] || LABELS.student;

  return (
    <div className="glass-card rounded-2xl p-6" data-testid="notification-preferences-card">
      <h3 className="font-semibold text-gray-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
        Notifications par email
      </h3>
      <p className="text-sm text-gray-500 mb-5">
        Les notifications dans l'application restent toujours actives. Ici, vous choisissez uniquement ce qui vous est envoyé par email.
      </p>

      {!prefs ? (
        <p className="text-sm text-gray-400">Chargement...</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {items.map(item => (
            <div key={item.key} className="flex items-start justify-between gap-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-900">{item.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
              <button
                data-testid={`pref-toggle-${item.key}`}
                onClick={() => toggle(item.key)}
                disabled={saving === item.key}
                aria-pressed={!!prefs[item.key]}
                className={`relative shrink-0 w-12 h-6 rounded-full transition-colors duration-200 ${
                  prefs[item.key] ? "bg-emerald-500" : "bg-gray-300"
                } disabled:opacity-50`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                    prefs[item.key] ? "translate-x-6" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
